// Ported from webapp2/src/config/adminSchema.ts.
// webapp2 imported JsonSchema/Metadata from a heavy dynamicTable component; here
// we define equivalent lightweight types locally so the schema + seed data are
// self-contained and can drive the Admin "Data Review" screen.

export interface JsonSchemaProperty {
  type: string;
  title: string;
  editable?: boolean;
  enum?: string[];
}

export interface JsonSchema {
  title: string;
  type: string;
  properties: Record<string, JsonSchemaProperty>;
}

export interface TableColumn {
  label: string;
  name: string;
  visible: boolean;
  width: number;
  type: 'text' | 'select' | string;
  options?: Record<string, any>;
}

export interface Metadata {
  properties: {
    tableField: {
      uiType: string;
      tableLayoutDefinition: TableColumn[];
    };
  };
}

export interface AdminRow {
  id: number;
  description: string;
  assetName: string;
  sourceTypeFrequency: string;
  sourceSystem: string;
  dataElement: string;
  domainTaskType: string;
  categorySubcategory: string;
  locationTypeRegion: string;
  dataProvider: string;
  L1: string;
  L2: string;
  L3: string;
  status: string;
  comment: string;
}

export const adminReviewSchema: JsonSchema = {
  title: 'Admin Review',
  type: 'object',
  properties: {
    id: { type: 'number', title: 'ID', editable: false },
    description: { type: 'string', title: 'Description', editable: false },
    assetName: { type: 'string', title: 'Asset Name', editable: false },
    sourceTypeFrequency: { type: 'string', title: 'Source Type / Frequency', editable: false },
    sourceSystem: { type: 'string', title: 'Source System', editable: false },
    dataElement: { type: 'string', title: 'Data Element', editable: false },
    domainTaskType: { type: 'string', title: 'Domain / Task Type', editable: false },
    categorySubcategory: { type: 'string', title: 'Category / Subcategory', editable: false },
    locationTypeRegion: { type: 'string', title: 'Location Type / Region', editable: false },
    dataProvider: { type: 'string', title: 'Data Provider', editable: false },
    L1: { type: 'string', title: 'L1 Reviewer', editable: true, enum: ['attest', 'reject', 'blank'] },
    L2: { type: 'string', title: 'L2 Reviewer', editable: true, enum: ['attest', 'reject', 'blank'] },
    L3: { type: 'string', title: 'L3 Reviewer', editable: true, enum: ['attest', 'reject', 'blank'] },
    status: {
      type: 'string',
      title: 'Status',
      editable: false,
      enum: [
        'provider_open',
        'provider_reviewed',
        'reviewer_L1',
        'reviewer_L2',
        'reviewer_L3',
        'completed',
      ],
    },
    comment: { type: 'string', title: 'comment', editable: true },
  },
};

const reviewerSelectOptions = [
  { label: 'Attest', value: 'attest' },
  { label: 'Reject', value: 'reject' },
  { label: 'Blank', value: 'blank' },
];

export const adminReviewMetadata: Metadata = {
  properties: {
    tableField: {
      uiType: 'Data Table',
      tableLayoutDefinition: [
        { label: 'ID', name: 'id', visible: true, width: 80, type: 'text', options: { editable: false } },
        { label: 'Description', name: 'description', visible: true, width: 180, type: 'text', options: { editable: false } },
        { label: 'Asset Name', name: 'assetName', visible: true, width: 150, type: 'text', options: { editable: false } },
        { label: 'Source Type / Frequency', name: 'sourceTypeFrequency', visible: true, width: 200, type: 'text', options: { editable: false } },
        { label: 'Source System', name: 'sourceSystem', visible: true, width: 150, type: 'text', options: { editable: false } },
        { label: 'Data Element', name: 'dataElement', visible: true, width: 200, type: 'text', options: { editable: false } },
        { label: 'Domain / Task Type', name: 'domainTaskType', visible: true, width: 180, type: 'text', options: { editable: false } },
        { label: 'Category / Subcategory', name: 'categorySubcategory', visible: true, width: 380, type: 'text', options: { editable: false } },
        { label: 'Location Type / Region', name: 'locationTypeRegion', visible: true, width: 300, type: 'text', options: { editable: false } },
        { label: 'Data Provider', name: 'dataProvider', visible: true, width: 160, type: 'text', options: { editable: false } },
        { label: 'L1 Reviewer', name: 'L1', visible: true, width: 120, type: 'select', options: { editable: true, selectOptions: reviewerSelectOptions } },
        { label: 'L2 Reviewer', name: 'L2', visible: true, width: 120, type: 'select', options: { editable: true, selectOptions: reviewerSelectOptions } },
        { label: 'L3 Reviewer', name: 'L3', visible: true, width: 120, type: 'select', options: { editable: true, selectOptions: reviewerSelectOptions } },
        {
          label: 'Status',
          name: 'status',
          visible: true,
          width: 150,
          type: 'select',
          options: {
            editable: false,
            selectOptions: [
              { label: 'Provider Open', value: 'provider_open' },
              { label: 'Provider Reviewed', value: 'provider_reviewed' },
              { label: 'Reviewer L1', value: 'reviewer_L1' },
              { label: 'Reviewer L2', value: 'reviewer_L2' },
              { label: 'Reviewer L3', value: 'reviewer_L3' },
              { label: 'Completed', value: 'completed' },
            ],
          },
        },
        { label: 'comment', name: 'comment', visible: true, width: 180, type: 'text', options: { editable: true } },
      ],
    },
  },
};

export const adminRows: AdminRow[] = [
  {
    id: 101,
    description: 'Marshall Station AGR',
    assetName: 'Marshall Station',
    sourceTypeFrequency: 'Manual / Monthly',
    sourceSystem: 'Manual',
    dataElement: 'Inlet Feed Gas Pressure',
    domainTaskType: 'Air / AGR',
    categorySubcategory: 'Acid Gas Removal Units [98.236(d)] / Utilization',
    locationTypeRegion: 'Facility: CNX Northern / SWPA West',
    dataProvider: 'Locke, Jim',
    L1: 'blank',
    L2: 'blank',
    L3: 'blank',
    status: 'provider_open',
    comment: '',
  },
  {
    id: 21867,
    description: 'MOR9 Pad Tank',
    assetName: 'MOR9 Pad',
    sourceTypeFrequency: 'Manual / Monthly',
    sourceSystem: 'Flowcal',
    dataElement: 'Separator Temperature',
    domainTaskType: 'Air / Water Tanks',
    categorySubcategory: 'Atmospheric Storage Tanks [98.236(j)] / Utilization',
    locationTypeRegion: 'Pad: CNX Southern / SWPA East',
    dataProvider: 'Locke, Jim',
    L1: 'blank',
    L2: 'blank',
    L3: 'blank',
    status: 'provider_open',
    comment: '',
  },
  {
    id: 21868,
    description: 'MOR9 Pad Tank',
    assetName: 'MOR9 Pad',
    sourceTypeFrequency: 'Manual / Monthly',
    sourceSystem: 'Flowcal',
    dataElement: 'Separator Pressure',
    domainTaskType: 'Air / Water Tanks',
    categorySubcategory: 'Atmospheric Storage Tanks [98.236(j)] / Utilization',
    locationTypeRegion: 'Pad: CNX Southern / SWPA East',
    dataProvider: 'Locke, Jim',
    L1: 'blank',
    L2: 'blank',
    L3: 'blank',
    status: 'provider_open',
    comment: '',
  },
  {
    id: 21869,
    description: 'NARDELL1 Pad Tank',
    assetName: 'NARDELL1 Pad',
    sourceTypeFrequency: 'Manual / Monthly',
    sourceSystem: 'Flowcal',
    dataElement: 'Separator Temperature',
    domainTaskType: 'Air / Water Tanks',
    categorySubcategory: 'Atmospheric Storage Tanks [98.236(j)] / Utilization',
    locationTypeRegion: 'Pad: CNX Northern / CPA',
    dataProvider: 'Locke, Jim',
    L1: 'blank',
    L2: 'blank',
    L3: 'blank',
    status: 'provider_open',
    comment: '',
  },
  {
    id: 21870,
    description: 'NARDELL1 Pad Tank',
    assetName: 'NARDELL1 Pad',
    sourceTypeFrequency: 'Manual / Monthly',
    sourceSystem: 'Flowcal',
    dataElement: 'Separator Pressure',
    domainTaskType: 'Air / Water Tanks',
    categorySubcategory: 'Atmospheric Storage Tanks [98.236(j)] / Utilization',
    locationTypeRegion: 'Pad: CNX Northern / CPA',
    dataProvider: 'Locke, Jim',
    L1: 'blank',
    L2: 'blank',
    L3: 'blank',
    status: 'provider_open',
    comment: '',
  },
  {
    id: 21871,
    description: 'NV13 Pad Tank',
    assetName: 'NV13 Pad',
    sourceTypeFrequency: 'Manual / Monthly',
    sourceSystem: 'Flowcal',
    dataElement: 'Separator Temperature',
    domainTaskType: 'Air / Water Tanks',
    categorySubcategory: 'Atmospheric Storage Tanks [98.236(j)] / Utilization',
    locationTypeRegion: 'Pad: CNX Southern / SWPA East',
    dataProvider: 'Locke, Jim',
    L1: 'blank',
    L2: 'blank',
    L3: 'blank',
    status: 'provider_open',
    comment: '',
  },
];
